import React, { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';

const CENTRO_PERU: [number, number] = [-12.046374, -77.042793];

const COLOR_VERDE = '#22C55E';
const COLOR_AMARILLO = '#F59E0B';
const COLOR_ROJO = '#EF4444';

interface Cliente {
  id: string;
  nombres: string;
  apellidos: string;
  direccion: string;
  telefono: string;
  lat: number | null;
  lng: number | null;
  estado: string;
  zona: string;
  cobrador: { id: string; nombre: string; apellido: string } | null;
  paquete: { nombre: string; precio: number } | null;
}

interface Factura {
  id: string;
  clienteId: string;
  estado: string;
  monto: number;
}

type EstadoMapa = 'verde' | 'amarillo' | 'rojo';

interface ClienteMapa extends Cliente {
  estadoMapa: EstadoMapa;
  color: string;
  deudaTotal: number;
}

const MapaClientes: React.FC = () => {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [facturas, setFacturas] = useState<Factura[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroZona, setFiltroZona] = useState('todas');
  const [filtroCobrador, setFiltroCobrador] = useState('todos');

  const cargar = async () => {
    setLoading(true);
    try {
      const [cliRes, facRes] = await Promise.all([
        api.get('/clientes'),
        api.get('/facturas'),
      ]);
      setClientes(cliRes.data);
      setFacturas(facRes.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  // Compute derived state
  const clientesMapa: ClienteMapa[] = useMemo(() => {
    return clientes
      .filter(c => c.lat != null && c.lng != null && c.lat !== 0 && c.lng !== 0)
      .map(c => {
        const facturasCliente = facturas.filter(f => f.clienteId === c.id);
        const tieneVencidas = facturasCliente.some(f => f.estado === 'vencido');
        const tienePendientes = facturasCliente.some(f => f.estado === 'pendiente');
        const deudaTotal = facturasCliente
          .filter(f => f.estado === 'pendiente' || f.estado === 'vencido')
          .reduce((s, f) => s + Number(f.monto), 0);

        let estadoMapa: EstadoMapa;
        let color: string;

        if (c.estado === 'cortado' || tieneVencidas) {
          estadoMapa = 'rojo';
          color = COLOR_ROJO;
        } else if (tienePendientes) {
          estadoMapa = 'amarillo';
          color = COLOR_AMARILLO;
        } else {
          estadoMapa = 'verde';
          color = COLOR_VERDE;
        }

        return { ...c, estadoMapa, color, deudaTotal };
      });
  }, [clientes, facturas]);

  // Unique zonas and cobradores for filters
  const zonas = useMemo(() => {
    const set = new Set(clientes.map(c => c.zona).filter(Boolean));
    return Array.from(set).sort();
  }, [clientes]);

  const cobradores = useMemo(() => {
    const map = new Map<string, string>();
    clientes.forEach(c => {
      if (c.cobrador) {
        map.set(c.cobrador.id, `${c.cobrador.nombre} ${c.cobrador.apellido}`);
      }
    });
    return Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
  }, [clientes]);

  // Apply filters
  const clientesFiltrados = useMemo(() => {
    return clientesMapa.filter(c => {
      if (filtroZona !== 'todas' && c.zona !== filtroZona) return false;
      if (filtroCobrador !== 'todos' && c.cobrador?.id !== filtroCobrador) return false;
      return true;
    });
  }, [clientesMapa, filtroZona, filtroCobrador]);

  const countVerde = clientesFiltrados.filter(c => c.estadoMapa === 'verde').length;
  const countAmarillo = clientesFiltrados.filter(c => c.estadoMapa === 'amarillo').length;
  const countRojo = clientesFiltrados.filter(c => c.estadoMapa === 'rojo').length;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ color: '#f1f5f9', fontWeight: 800, fontSize: 22, margin: 0 }}>📍 Mapa Clientes</h2>
          <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 13 }}>Distribución geográfica y estado de pago</p>
        </div>
        <button onClick={cargar}
          style={{ background: '#0ea5e922', border: '1px solid #0ea5e944', borderRadius: 8, padding: '8px 16px', color: '#38bdf8', cursor: 'pointer', fontSize: 13 }}>
          ↻ Actualizar
        </button>
      </div>

      {/* Contadores */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        <div style={{ background: '#1e293b', borderRadius: 10, padding: '12px 16px', border: `1px solid ${COLOR_VERDE}33` }}>
          <div style={{ color: '#64748b', fontSize: 12 }}>Al día</div>
          <div style={{ color: COLOR_VERDE, fontSize: 22, fontWeight: 800 }}>{countVerde}</div>
        </div>
        <div style={{ background: '#1e293b', borderRadius: 10, padding: '12px 16px', border: `1px solid ${COLOR_AMARILLO}33` }}>
          <div style={{ color: '#64748b', fontSize: 12 }}>Pendientes</div>
          <div style={{ color: COLOR_AMARILLO, fontSize: 22, fontWeight: 800 }}>{countAmarillo}</div>
        </div>
        <div style={{ background: '#1e293b', borderRadius: 10, padding: '12px 16px', border: `1px solid ${COLOR_ROJO}33` }}>
          <div style={{ color: '#64748b', fontSize: 12 }}>Vencidos / Cortados</div>
          <div style={{ color: COLOR_ROJO, fontSize: 22, fontWeight: 800 }}>{countRojo}</div>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={filtroZona} onChange={e => setFiltroZona(e.target.value)}
          style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '8px 14px', color: 'white', fontSize: 13, minWidth: 160 }}>
          <option value="todas">Todas las zonas</option>
          {zonas.map(z => <option key={z} value={z}>{z}</option>)}
        </select>
        <select value={filtroCobrador} onChange={e => setFiltroCobrador(e.target.value)}
          style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '8px 14px', color: 'white', fontSize: 13, minWidth: 200 }}>
          <option value="todos">Todos los cobradores</option>
          {cobradores.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16, fontSize: 13 }}>
          <span><span style={{ color: COLOR_VERDE }}>●</span> Al día</span>
          <span><span style={{ color: COLOR_AMARILLO }}>●</span> Pendiente</span>
          <span><span style={{ color: COLOR_ROJO }}>●</span> Vencido/Cortado</span>
          <span style={{ color: '#64748b' }}>({clientesFiltrados.length} en mapa)</span>
        </div>
      </div>

      {/* Mapa */}
      <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #1e293b', height: 'calc(100vh - 310px)', minHeight: 400 }}>
        {loading ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#475569' }}>
            Cargando mapa...
          </div>
        ) : (
          <MapContainer center={CENTRO_PERU} zoom={12} style={{ height: '100%', width: '100%' }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
            {clientesFiltrados.map(c => (
              <CircleMarker
                key={c.id}
                center={[Number(c.lat), Number(c.lng)]}
                radius={8}
                pathOptions={{
                  fillColor: c.color,
                  fillOpacity: 0.85,
                  color: '#fff',
                  weight: 1.5,
                }}
              >
                <Popup>
                  <div style={{ minWidth: 190 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{c.nombres} {c.apellidos}</div>
                    {c.direccion && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{c.direccion}</div>}
                    {c.telefono && <div style={{ fontSize: 12, marginTop: 2 }}>📞 {c.telefono}</div>}
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      Estado: <span style={{ color: c.color, fontWeight: 700 }}>
                        {c.estadoMapa === 'verde' ? 'Al día' : c.estadoMapa === 'amarillo' ? 'Pendiente' : 'Vencido/Cortado'}
                      </span>
                    </div>
                    {c.deudaTotal > 0 && (
                      <div style={{ fontSize: 12, marginTop: 2, color: c.color }}>
                        Deuda: <strong>S/. {c.deudaTotal.toFixed(2)}</strong>
                      </div>
                    )}
                    {c.zona && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Zona: {c.zona}</div>}
                    {c.cobrador && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                        Cobrador: {c.cobrador.nombre} {c.cobrador.apellido}
                      </div>
                    )}
                    {c.paquete && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                        Paquete: {c.paquete.nombre}
                      </div>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        )}
      </div>
    </div>
  );
};

export default MapaClientes;
